import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, TextField, Typography, Button } from "@material-ui/core";
import { Controller, useFormContext } from "react-hook-form";
import { EMAIL_TAGS } from "./emailTags";
import { useEmailTemplateStyles } from "./styles";
import {
  valueToHtml,
  htmlToValue,
  placeCursorAtEnd,
  placeCursorAtNode,
  removeDuplicateChips,
  insertLineBreak,
  getAdjacentChip,
  removeChipAndPlaceCursor,
} from "../../utils/emailTemplateEditor/emailTemplateEditorUtils";

const TemplateBodyField = ({ value, onChange, label, testId }) => {
  const classes = useEmailTemplateStyles();
  const editorRef = useRef(null);
  const internalChange = useRef(false);
  const [focused, setFocused] = React.useState(false);

  useEffect(() => {
    if (internalChange.current) {
      internalChange.current = false;
      return;
    }
    if (editorRef.current) {
      editorRef.current.innerHTML = valueToHtml(value || "");
      placeCursorAtEnd(editorRef.current);
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    removeDuplicateChips(editorRef.current);
    internalChange.current = true;
    const val = htmlToValue(editorRef.current).replace(/^\n+$/, "");
    onChange(val);
  }, [onChange]);

  const handleMouseDown = useCallback(
    (e) => {
      const deleteBtn = e.target.closest(".email-tag-chip-delete");
      if (!deleteBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const chip = deleteBtn.closest(".email-tag-chip");
      if (chip && editorRef.current) {
        const next = chip.nextSibling;
        chip.parentNode.removeChild(chip);
        placeCursorAtNode(next, editorRef.current);
        editorRef.current.focus();
        emitChange();
      }
    },
    [emitChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (insertLineBreak()) emitChange();
        return;
      }
      if (e.key === "Backspace") {
        const chip = getAdjacentChip("before");
        if (chip) {
          e.preventDefault();
          removeChipAndPlaceCursor(chip);
          emitChange();
        }
      }
      if (e.key === "Delete") {
        const chip = getAdjacentChip("after");
        if (chip) {
          e.preventDefault();
          removeChipAndPlaceCursor(chip);
          emitChange();
        }
      }
    },
    [emitChange]
  );

  const handleContainerClick = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  return (
    <div className={classes.templateContainer}>
      <div
        className={classes.templateBody}
        onClick={handleContainerClick}
        data-testid={testId}
      >
        <span
          className={`${classes.templateLabel} ${focused ? classes.templateLabelFocused : ""}`}
        >
          {label}
        </span>
        <div
          ref={editorRef}
          className={classes.editableArea}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          data-testid={`${testId}-editable`}
        />
      </div>
    </div>
  );
};

const EmailTemplateEditor = ({ title, subjectFieldName, bodyFieldName, control }) => {
  const classes = useEmailTemplateStyles();
  const { setValue, getValues, trigger } = useFormContext();

  const handleInsertTag = useCallback(
    (tagValue) => {
      const currentValue = getValues(bodyFieldName) || "";
      const newValue = currentValue + tagValue;
      setValue(bodyFieldName, newValue, { shouldDirty: true });
      trigger(bodyFieldName);
    },
    [bodyFieldName, setValue, getValues, trigger]
  );

  return (
    <div data-testid={`email-template-${bodyFieldName}`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name={subjectFieldName}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={`${title} Subject`}
                variant="outlined"
                fullWidth
                data-testid={`${subjectFieldName}-input`}
                inputProps={{ "data-testid": `${subjectFieldName}` }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={bodyFieldName}
            control={control}
            render={({ field }) => (
              <TemplateBodyField
                value={field.value}
                onChange={field.onChange}
                label={`${title} Template`}
                testId={`${bodyFieldName}-input`}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="body2">Tags:</Typography>
            </Grid>
            {EMAIL_TAGS.map((tag) => (
              <Grid item key={tag.value}>
                <Button
                  variant="contained"
                  size="small"
                  className={classes.tagButton}
                  onClick={() => handleInsertTag(tag.value)}
                  data-testid={`tag-btn-${tag.label}`}
                  title={tag.description}
                >
                  {tag.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

EmailTemplateEditor.propTypes = {
  title: PropTypes.string.isRequired,
  subjectFieldName: PropTypes.string.isRequired,
  bodyFieldName: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
};

export default EmailTemplateEditor;
